<script setup lang="ts">
import { useClipboard } from "@vueuse/core";
import { toast } from "vue-sonner";

const props = withDefaults(
  defineProps<{
    id: string;
    label: string;
    icon: string;
    path?: string;
    showHierarchyAction?: boolean;
  }>(),
  {
    path: "",
    showHierarchyAction: false,
  },
);

const emit = defineEmits<{
  showHierarchy: [];
}>();

const {
  copy: copyId,
  copied: idCopied,
} = useClipboard({
  source: () => props.id,
  copiedDuring: 1500,
  legacy: true,
});
const {
  copy: copyPath,
  copied: pathCopied,
} = useClipboard({
  source: () => props.path,
  copiedDuring: 1500,
  legacy: true,
});

async function copyValue(kind: "id" | "path") {
  try {
    if (kind === "id") {
      await copyId();
      toast.success("Block ID copied to clipboard");
      return;
    }

    if (!props.path) return;
    await copyPath();
    toast.success("Block path copied to clipboard");
  } catch {
    toast.error("Failed to copy to clipboard");
  }
}
</script>

<template>
  <div class="w-[320px]">
    <div class="flex items-center gap-2.5">
      <div class="grid size-8 shrink-0 place-items-center rounded-md bg-muted">
        <Icon :name="icon" mode="svg" size="16" class="text-muted-foreground" />
      </div>
      <div class="min-w-0">
        <div class="truncate text-[13px] font-semibold text-foreground">
          {{ label }}
        </div>
        <div class="text-[11.5px] text-muted-foreground">
          Building block
        </div>
      </div>
    </div>

    <Separator class="my-3" />

    <dl class="space-y-2.5">
      <div class="space-y-1">
        <dt class="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          ID
        </dt>
        <dd class="flex items-center gap-1 rounded-md border bg-muted/45 py-1 pl-2.5 pr-1">
          <code class="min-w-0 flex-1 truncate font-mono text-[11.5px] text-foreground">
            {{ id }}
          </code>
          <Button
            variant="ghost"
            size="icon"
            class="size-7 shrink-0"
            :title="idCopied ? 'Copied' : 'Copy block ID'"
            @click.stop="copyValue('id')"
          >
            <Icon
              mode="svg"
              size="14"
              :name="idCopied ? 'lucide:check' : 'lucide:copy'"
            />
            <span class="sr-only">{{ idCopied ? "Copied" : "Copy block ID" }}</span>
          </Button>
        </dd>
      </div>

      <div class="space-y-1">
        <dt class="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Resolved path
        </dt>
        <dd class="flex items-center gap-1 rounded-md border bg-muted/45 py-1 pl-2.5 pr-1">
          <code
            class="min-w-0 flex-1 break-all font-mono text-[11.5px]"
            :class="path ? 'text-foreground' : 'text-muted-foreground'"
          >
            {{ path || "No resolved path" }}
          </code>
          <Button
            variant="ghost"
            size="icon"
            class="size-7 shrink-0"
            :disabled="!path"
            :title="pathCopied ? 'Copied' : 'Copy resolved path'"
            @click.stop="copyValue('path')"
          >
            <Icon
              mode="svg"
              size="14"
              :name="pathCopied ? 'lucide:check' : 'lucide:copy'"
            />
            <span class="sr-only">
              {{ pathCopied ? "Copied" : "Copy resolved path" }}
            </span>
          </Button>
        </dd>
      </div>
    </dl>

    <template v-if="showHierarchyAction">
      <Separator class="my-3" />
      <Button
        variant="ghost"
        size="sm"
        class="w-full justify-start"
        @click.stop="emit('showHierarchy')"
      >
        <Icon mode="svg" size="15" name="lucide:list-tree" class="mr-2" />
        Show in hierarchy
      </Button>
    </template>
  </div>
</template>
